import { useCallback } from 'preact/hooks'
import { useAssetWebsterChat } from './useAssetWebsterChat'
import { useEmailAssetSignalR } from './useEmailAssetSignalR'
import * as aiChat from '@/api/aiChat'
import type { AIConversationContext } from '@/types/aiChat'
import { ChatEntityTypes, ChatLocations, ChatViewModes } from '@/constants/aiChat'

interface UseEmailAssetChatOptions {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    assetId: string
    assetType: 'Email Template' | 'Email Copy'
    /** Existing conversation session id from the asset (DraftConversationSessionId).
     *  When provided, chat history is restored from the server on mount. */
    initialSessionId?: string | null
    /** Fired when the server signals the draft has been rebuilt. The handler is
     *  expected to refetch the asset and update the editor — the SignalR event
     *  no longer carries the HTML payload. */
    onDraftRebuilt?: () => void
    /** Fired when a SignalR `error` event lands during generation. The handler
     *  is expected to refresh the asset so DraftGenerationStatus picks up the
     *  Failed value the SB worker just persisted, which flips EmailBuilder
     *  into the failed-state UI without waiting for the user to refresh. */
    onGenerationError?: () => void
    /** Fired when the server refused the edit and kept the draft. The build is over, so the handler
     *  ends the generating state the same way a `rebuilt` would. */
    onRebuildRejected?: () => void
}

/**
 * Webster chat for email asset editing. Composes the shared chat lifecycle
 * (session, history, sendMessage, standard rebuild events) with email-specific
 * session creation and rebuild handling.
 */
export function useEmailAssetChat({
    clientCompanyId,
    workspaceId,
    campaignId,
    assetId,
    assetType,
    initialSessionId,
    onDraftRebuilt,
    onGenerationError,
    onRebuildRejected,
}: UseEmailAssetChatOptions) {
    const createSession = useCallback(async () => {
        const context: AIConversationContext = {
            campaignId,
            assetId,
            location: ChatLocations.EmailAssetEditor,
            entityId: assetId,
            entityType: ChatEntityTypes.Asset,
            viewMode: ChatViewModes.Edit,
            availableFields: [],
            availableActions: [],
            loadedData: { assetType },
        }
        const session = await aiChat.startConversation(clientCompanyId, workspaceId, context)
        return session.Id
    }, [clientCompanyId, workspaceId, campaignId, assetId, assetType])

    const assetChat = useAssetWebsterChat({
        clientCompanyId,
        workspaceId,
        initialSessionId,
        createSession,
    })

    // Live counter was removed — the per-message token footer surfaces model +
    // tokens on the finalized Webster bubble after the turn ends (see
    // MessageTokenFooter). The streaming indicator now shows elapsed-time
    // and a rotating status phrase only, which is what carries the "still
    // working" signal for asset generations that can run minutes long.

    // Email-asset SignalR fan-out filters content events server-side, so the
    // chat hook only needs status callbacks here — no per-chunk char tally.

    // onRebuilt's job here is just lifecycle: clear the working indicator,
    // setGenerating(false), trigger the asset refetch. The visible Webster
    // reply (Opus's personalized <message> tag) is delivered by the server's
    // assistantMessageAppended SignalR event, which lands BEFORE rebuilt
    // because it broadcasts inside GenerateViaAgentAsync. The old client-side
    // EMAIL_TEMPLATE_COMPLETION_MESSAGES / EMAIL_COPY_COMPLETION_MESSAGES
    // pools are gone — when the persisted record and the live UI diverged
    // (random pool client-side, ghost row server-side), reloads showed a
    // different message than the live UI did.
    const handleRebuilt = useCallback(() => {
        assetChat.onRebuilt()
        onDraftRebuilt?.()
    }, [assetChat, onDraftRebuilt])

    const handleRebuildRejected = useCallback((content: string) => {
        assetChat.onRebuildRejected(content)
        onRebuildRejected?.()
    }, [assetChat, onRebuildRejected])

    useEmailAssetSignalR({
        assetId,
        onAnalyzing: assetChat.onAnalyzing,
        onRebuilding: assetChat.onRebuilding,
        onRebuilt: handleRebuilt,
        // Unlike onRebuilt, a rejection leaves the draft untouched, so it skips onDraftRebuilt's
        // reseed. It still ends the build, which a builder opened mid-run is waiting on.
        onRebuildRejected: handleRebuildRejected,
        onConversationalResponse: assetChat.onConversationalResponse,
        onAssistantMessageAppended: assetChat.onAssistantMessageAppended,
        onError: (message: string) => {
            assetChat.onError(message)
            // Refresh the asset so the EmailBuilder picks up the Failed status
            // the SB worker just persisted on its catch block. Without this,
            // DraftGenerationStatus stays "Generating" client-side until the
            // user manually refreshes the page or revisits the asset.
            onGenerationError?.()
        },
        // Multi-user turn-lock: drives lockedByUserName state for spectators and
        // appends peer messages so the chat is observable in real time across
        // every editor on this email asset.
        onChatStateChanged: assetChat.onChatStateChanged,
        onUserMessageAppended: assetChat.onUserMessageAppended,
    })

    return {
        messages: assetChat.messages,
        generating: assetChat.generating,
        hasMessages: assetChat.hasMessages,
        sendMessage: assetChat.sendMessage,
        beginAutoGeneration: assetChat.beginAutoGeneration,
        // Multi-user turn-lock state. Consumer compares turnUserId against the
        // current user id to compute lockedByUserName for the input.
        turnState: assetChat.turnState,
        turnUserId: assetChat.turnUserId,
        turnUserName: assetChat.turnUserName,
    }
}
